import { normalizeAppLanguage } from "util/locale";
import {
  DEFAULT_CALLING_HOURS,
  DEFAULT_PHONE_TRIGGER,
  makeDefaultDistribution,
  OPEN_TEXT_LENGTH,
  QUESTION_TYPE,
  RATING_SCALE,
  SURVEY_FLOW_MODE,
  SURVEY_RESPONSE_MODE,
} from "./constants";
import {
  snapshotFormToLocale,
  translationsFromConfig,
  translationsToConfigPayload,
} from "./surveyTranslations";
import { urlDomain, videoDurationLimits } from "./surveyWidgetFlow";

const genId = (prefix = "q") =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// The survey link name, normalised as the API does (feedback_repo.normalize_custom_slug):
// "Pivony Stajyer 2. Aşama" → "pivony-stajyer-2-asama". "" when it can't be a link (3–64 characters).
const SLUG_LETTERS = { ç: "c", ğ: "g", ı: "i", ö: "o", ş: "s", ü: "u", â: "a", î: "i", û: "u", ä: "a", ß: "s" };

export function normalizeLinkSlug(raw) {
  const slug = String(raw || "")
    .trim()
    .replace(/İ/g, "i")
    .toLowerCase()
    .replace(/[çğıöşüâîûäß]/g, (ch) => SLUG_LETTERS[ch])
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return /^[a-z0-9][a-z0-9-]{2,63}$/.test(slug) ? slug : "";
}

export function publicSurveyUrl(slug) {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/s/${slug}`;
  }
  return `https://app.pivony.com/s/${slug}`;
}

// "2026-11-02T20:59:00Z" → "2026-11-02T23:59": a UTC moment as wall-clock time in a time zone.
function localInZone(iso, zone) {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return "";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(moment)
      .map((p) => [p.type, p.value])
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

// The phone channel's calling hours and CRM trigger: camelCase in the builder, snake_case on
// the wire. The webhook token never travels in the channel config (it has its own endpoint).
function callingHoursToApi(hours) {
  const h = { ...DEFAULT_CALLING_HOURS, ...(hours || {}) };
  return { start: h.start, end: h.end, days_off: h.daysOff, skip_holidays: h.skipHolidays !== false };
}

function callingHoursFromApi(hours) {
  const h = hours || {};
  return {
    start: h.start || DEFAULT_CALLING_HOURS.start,
    end: h.end || DEFAULT_CALLING_HOURS.end,
    daysOff: Array.isArray(h.days_off) ? h.days_off : DEFAULT_CALLING_HOURS.daysOff,
    skipHolidays: h.skip_holidays !== false,
  };
}

function phoneTriggerToApi(trigger) {
  const tr = { ...DEFAULT_PHONE_TRIGGER, ...(trigger || {}) };
  return {
    enabled: !!tr.enabled,
    delay_hours: tr.delayHours,
    max_attempts: tr.maxAttempts,
    retry_after_hours: tr.retryAfterHours,
    frequency_days: tr.frequencyDays,
  };
}

function phoneTriggerFromApi(trigger) {
  const tr = trigger || {};
  const num = (value, fallback) => (Number.isFinite(value) ? value : fallback);
  return {
    enabled: !!tr.enabled,
    delayHours: num(tr.delay_hours, DEFAULT_PHONE_TRIGGER.delayHours),
    maxAttempts: num(tr.max_attempts, DEFAULT_PHONE_TRIGGER.maxAttempts),
    retryAfterHours: num(tr.retry_after_hours, DEFAULT_PHONE_TRIGGER.retryAfterHours),
    frequencyDays: num(tr.frequency_days, DEFAULT_PHONE_TRIGGER.frequencyDays),
  };
}

export function distributionToChannelPayload(distribution) {
  const d = { ...makeDefaultDistribution(), ...(distribution || {}) };
  return [
    {
      channel_type: "link_qr",
      enabled: !!d.link_qr?.enabled,
      config: {
        // The deadline and the response limit are the survey's own now ("Close automatically").
        expires_at: null,
        max_responses: null,
        language_mode: d.link_qr?.languageMode || "auto",
        fixed_language:
          d.link_qr?.languageMode === "fixed" ? d.link_qr?.fixedLanguage : null,
      },
    },
    {
      channel_type: "web_embed",
      enabled: !!d.web_embed?.enabled,
      config: {
        display_mode: d.web_embed?.displayMode || "popup",
        trigger_preset:
          d.web_embed?.triggerPreset === "page_load"
            ? "page_load"
            : d.web_embed?.triggerPreset === "launcher_click"
              ? "launcher_click"
              : "custom",
        trigger_rules:
          d.web_embed?.triggerPreset === "page_load" ||
          d.web_embed?.triggerPreset === "launcher_click"
            ? []
            : (d.web_embed?.triggerRules || []).map((r) => ({
                rule_type: r.rule_type,
                value: r.value,
              })),
        once_per_day: !!d.web_embed?.oncePerDay,
        device_target: d.web_embed?.deviceTarget || "all",
        url_match_pattern: d.web_embed?.urlMatch || null,
        launcher_label: (d.web_embed?.launcherLabel || "").trim() || null,
        launcher_position:
          d.web_embed?.launcherPosition === "left" ? "left" : "right",
      },
    },
    {
      channel_type: "triggered_send",
      enabled: !!d.triggered_send?.enabled,
      config: {
        event_source: d.triggered_send?.eventSource || null,
        event_name: d.triggered_send?.eventName || null,
        delay_minutes: d.triggered_send?.delayMinutes || 0,
        frequency_cap: {
          max_per_customer: d.triggered_send?.freqMax || 1,
          per_days: d.triggered_send?.freqDays || 30,
        },
        channel_for_send: d.triggered_send?.channelForSend || "email",
      },
    },
    {
      channel_type: "in_app_sdk",
      enabled: !!d.in_app_sdk?.enabled,
      config: {},
    },
    // Only once used, so plans without phone surveys never create the row.
    ...(d.phone_call?.enabled || d.phone_call?.exists
      ? [
          {
            channel_type: "phone_call",
            enabled: !!d.phone_call.enabled,
            config: {
              language: d.phone_call.language || null,
              persona: d.phone_call.persona || "friendly_empathetic",
              context: (d.phone_call.context || "").trim(),
              greeting: (d.phone_call.greeting || "").trim(),
              disclosure: (d.phone_call.disclosure || "").trim(),
              knowledge: (d.phone_call.knowledge || "").trim(),
              voice_id: d.phone_call.voiceId || "",
              voice_model: d.phone_call.voiceModel || "",
              brand_name: (d.phone_call.brandName || "").trim(),
              calling_hours: callingHoursToApi(d.phone_call.callingHours),
              trigger: phoneTriggerToApi(d.phone_call.trigger),
            },
          },
        ]
      : []),
  ];
}

export function channelsToDistribution(channels) {
  const base = makeDefaultDistribution();
  (channels || []).forEach((ch) => {
    const cfg = ch.config || {};
    if (ch.channel_type === "link_qr") {
      const slug = ch.slug || cfg.slug || "";
      base.link_qr = {
        ...base.link_qr,
        enabled: !!ch.enabled,
        slug,
        fullUrl: cfg.full_url || (slug ? publicSurveyUrl(slug) : ""),
        languageMode: cfg.language_mode || "auto",
        fixedLanguage: cfg.fixed_language || "en",
        kioskKey: cfg.kiosk_key || "",
      };
    }
    if (ch.channel_type === "phone_call") {
      base.phone_call = {
        ...base.phone_call,
        enabled: !!ch.enabled,
        exists: true,
        language: cfg.language || "",
        persona: cfg.persona || "friendly_empathetic",
        context: cfg.context || "",
        greeting: cfg.greeting || "",
        disclosure: cfg.disclosure || "",
        knowledge: cfg.knowledge || "",
        voiceId: cfg.voice_id || "",
        voiceModel: cfg.voice_model === "eleven_v4_turbo" ? cfg.voice_model : "",
        brandName: cfg.brand_name || "",
        callingHours: callingHoursFromApi(cfg.calling_hours),
        trigger: phoneTriggerFromApi(cfg.trigger),
      };
    }
    if (ch.channel_type === "web_embed") {
      const rules = Array.isArray(cfg.trigger_rules) ? cfg.trigger_rules : [];
      const triggerPreset =
        cfg.trigger_preset === "page_load"
          ? "page_load"
          : cfg.trigger_preset === "launcher_click"
            ? "launcher_click"
            : "custom";
      base.web_embed = {
        ...base.web_embed,
        enabled: !!ch.enabled,
        displayMode: cfg.display_mode || "popup",
        triggerPreset,
        oncePerDay: cfg.once_per_day === true,
        deviceTarget: cfg.device_target || "all",
        triggerRules:
          triggerPreset === "custom" ? rules : [],
        urlMatch: cfg.url_match_pattern || "",
        launcherLabel: cfg.launcher_label || "",
        launcherPosition:
          cfg.launcher_position === "left" ? "left" : "right",
      };
    }
    if (ch.channel_type === "triggered_send") {
      const freq = cfg.frequency_cap || {};
      base.triggered_send = {
        ...base.triggered_send,
        enabled: !!ch.enabled,
        eventSource: cfg.event_source || "sonic",
        eventName: cfg.event_name || "",
        delayMinutes: cfg.delay_minutes || 0,
        freqMax: freq.max_per_customer || 1,
        freqDays: freq.per_days || 30,
        channelForSend: cfg.channel_for_send || "email",
      };
    }
    if (ch.channel_type === "in_app_sdk") {
      base.in_app_sdk = { ...base.in_app_sdk, enabled: !!ch.enabled };
    }
  });
  return base;
}

export const AGENT_NOTE_MAX = 500;
// A voice question's topic ties it to the company knowledge base's guidelines on that topic.
export const QUESTION_TOPIC_MAX = 60;

function questionTopic(topic) {
  return String(topic || "").trim().replace(/\s+/g, " ").slice(0, QUESTION_TOPIC_MAX);
}

export function normalizeQuestions(questions) {
  return (questions || []).map((q) => ({
    id: q.id || genId("q"),
    type: q.type || QUESTION_TYPE.OPEN_TEXT,
    text: (q.text || "").trim(),
    required: !!q.required,
    ratingScale: q.ratingScale || q.scale || RATING_SCALE.ONE_TO_FIVE,
    options: Array.isArray(q.options)
      ? q.options.map((o) => (o || "").trim()).filter(Boolean)
      : [],
    ...((q.type || QUESTION_TYPE.OPEN_TEXT) === QUESTION_TYPE.OPEN_TEXT &&
    q.answerLength === OPEN_TEXT_LENGTH.SHORT
      ? { answerLength: OPEN_TEXT_LENGTH.SHORT }
      : {}),
    ...(q.type === QUESTION_TYPE.VIDEO ? videoQuestionFields(q) : {}),
    ...(q.type === QUESTION_TYPE.URL && urlDomain(q.urlDomain) ? { urlDomain: urlDomain(q.urlDomain) } : {}),
    // What the voice agent should do with this question (follow-ups); never shown to respondents.
    ...((q.agentNote || "").trim() ? { agentNote: q.agentNote.trim().slice(0, AGENT_NOTE_MAX) } : {}),
    ...(questionTopic(q.topic) ? { topic: questionTopic(q.topic) } : {}),
    // Linked to a company question bank question, whose wording the API keeps it in.
    ...(Number(q.bankId) > 0 ? { bankId: Number(q.bankId) } : {}),
  }));
}

function videoQuestionFields(q) {
  const { min, max } = videoDurationLimits(q);
  const instructions = (q.videoInstructions || "").trim();
  return { videoMinSeconds: min, videoMaxSeconds: max, ...(instructions ? { videoInstructions: instructions } : {}) };
}

// Survey-level settings of video answers (see pivony-api survey_video).
function videoSettingsFromConfig(cfg) {
  return {
    videoRetentionDays: Number(cfg.video_retention_days) || 90,
    videoTranscript: cfg.video_transcript === true,
    videoContactEmail: cfg.video_contact_email || "",
  };
}

// "Ask for consent before starting": the owner's text and privacy link. The API numbers the
// wording (start_consent_version); the builder only shows that number.
function startConsentFromConfig(cfg) {
  const consent = cfg.start_consent || {};
  return {
    startConsent: consent.enabled === true,
    startConsentText: consent.text || "",
    startConsentPrivacyUrl: consent.privacy_url || "",
    startConsentVersion: Number(cfg.start_consent_version) || 0,
  };
}

// "Closing": deadline in a time zone, response limit, closed message (see pivony-api survey_close).
// A link that still has its old end date or response limit (set on the Distribute step before
// these settings existed) brings them here, so saving the survey keeps them.
function closeSettingsFromConfig(cfg, channels) {
  if (!cfg.close_local && !cfg.close_max_responses) {
    const link = (channels || []).find((ch) => ch.channel_type === "link_qr")?.config || {};
    const closeLocal = link.expires_at ? localInZone(link.expires_at, "Europe/Istanbul") : "";
    const closeMaxResponses = Number(link.max_responses) || null;
    if (closeLocal || closeMaxResponses) {
      return {
        ...closeSettingsFromConfig({ ...cfg, close_local: closeLocal, close_max_responses: closeMaxResponses }, []),
        closeTimezone: "Europe/Istanbul",
      };
    }
  }
  return {
    closeEnabled: !!(cfg.close_local || cfg.close_max_responses),
    closeLocal: cfg.close_local || "",
    closeTimezone: cfg.close_timezone || "Europe/Istanbul",
    closeMaxResponses: Number(cfg.close_max_responses) || null,
    closedMessage: cfg.closed_message || "",
    showDeadline: cfg.show_deadline === true,
    closeReminder: cfg.close_reminder === true,
    closeGraceMinutes: cfg.close_grace_minutes ?? 60,
    autoClosed: !!cfg.auto_closed,
  };
}

function closeSettingsToConfig(survey) {
  const on = !!survey.closeEnabled;
  return {
    close_local: on ? survey.closeLocal || "" : "",
    close_timezone: survey.closeTimezone || "Europe/Istanbul",
    close_max_responses: on && survey.closeMaxResponses ? Number(survey.closeMaxResponses) : null,
    closed_message: (survey.closedMessage || "").trim(),
    show_deadline: on && !!survey.showDeadline,
    close_reminder: on && !!survey.closeReminder,
    close_grace_minutes: Number.isFinite(Number(survey.closeGraceMinutes)) ? Number(survey.closeGraceMinutes) : 60,
  };
}

// Written unless the survey is answered by voice (asked by the AI agent).
function responseModeOf(cfg) {
  return cfg.response_mode === SURVEY_RESPONSE_MODE.VOICE ? SURVEY_RESPONSE_MODE.VOICE : SURVEY_RESPONSE_MODE.TEXT;
}

// What a voice survey's calls open with, worked out by the API from the survey's own greeting,
// else the saved voice greeting, else the default: callGreeting today, defaultCallGreeting when
// the survey has none of its own (what the builder shows once its greeting field is empty).
function callGreetings(inst) {
  return {
    callGreeting: inst?.call_greeting || "",
    defaultCallGreeting: inst?.default_call_greeting || "",
  };
}

export function mapInstrumentToSurvey(inst) {
  const cfg = inst?.config || {};
  const multilingualEnabled = cfg.multilingual_enabled === true;

  if (!multilingualEnabled) {
    return {
      id: inst.id,
      name: inst.title || "",
      description: inst.description || "",
      status: inst.status || "draft",
      closedByPlan: inst.closed_by_plan === true,
      ...callGreetings(inst),
      widgetHeaderTitle: cfg.widget_header_title || "",
      showBranding: cfg.show_branding !== false,
      requestContactDetails: cfg.request_contact_details === true,
      contactDetailsRequired: cfg.contact_details_required === true,
      requestConsent: cfg.request_consent === true,
      ...startConsentFromConfig(cfg),
      ...closeSettingsFromConfig(cfg, inst.channels),
      oneResponsePerEmail: cfg.one_response_per_email === true,
      ...videoSettingsFromConfig(cfg),
      primaryColor: cfg.primary_color || "#6366f1",
      logoUrl: cfg.logo_url || "",
      popupPosition: cfg.popup_position || "bottom_right",
      flowMode: cfg.flow_mode || SURVEY_FLOW_MODE.STEPPED,
      responseMode: responseModeOf(cfg),
      thankYouMessage: cfg.thank_you_message || "",
      multilingualEnabled: false,
      primaryLanguage: normalizeAppLanguage(cfg.primary_language || "en"),
      editingLanguage: normalizeAppLanguage(cfg.primary_language || "en"),
      translations: {},
      questions: normalizeQuestions(cfg.questions),
      distribution: channelsToDistribution(inst.channels),
      createdAt: inst.created_at,
      updatedAt: inst.updated_at,
      responseCount: cfg.response_count || 0,
      lastResponseAt: cfg.last_response_at || null,
      viewCount: cfg.view_count || 0,
      startCount: cfg.start_count || 0,
    };
  }

  const { primaryLanguage, translations } = translationsFromConfig(cfg);
  const primaryLocale = translations[primaryLanguage] || {};
  const baseQuestions = normalizeQuestions(cfg.questions);
  const localizedQuestions = baseQuestions.map((q) => {
    const translated = primaryLocale.questions?.[q.id];
    if (!translated) return q;
    return {
      ...q,
      text: translated.text || q.text,
      options:
        Array.isArray(translated.options) && translated.options.length
          ? translated.options
          : q.options,
    };
  });

  return {
    id: inst.id,
    name: primaryLocale.name || inst.title || "",
    description: primaryLocale.description || inst.description || "",
    status: inst.status || "draft",
    closedByPlan: inst.closed_by_plan === true,
    ...callGreetings(inst),
    widgetHeaderTitle:
      primaryLocale.widgetHeaderTitle || cfg.widget_header_title || "",
    showBranding: cfg.show_branding !== false,
    requestContactDetails: cfg.request_contact_details === true,
    contactDetailsRequired: cfg.contact_details_required === true,
    requestConsent: cfg.request_consent === true,
    ...startConsentFromConfig(cfg),
    ...closeSettingsFromConfig(cfg, inst.channels),
    oneResponsePerEmail: cfg.one_response_per_email === true,
    ...videoSettingsFromConfig(cfg),
    primaryColor: cfg.primary_color || "#6366f1",
    logoUrl: cfg.logo_url || "",
    popupPosition: cfg.popup_position || "bottom_right",
    flowMode: cfg.flow_mode || SURVEY_FLOW_MODE.STEPPED,
    responseMode: responseModeOf(cfg),
    thankYouMessage: cfg.thank_you_message || "",
    multilingualEnabled: true,
    primaryLanguage,
    editingLanguage: primaryLanguage,
    translations,
    questions: localizedQuestions,
    distribution: channelsToDistribution(inst.channels),
    createdAt: inst.created_at,
    updatedAt: inst.updated_at,
    responseCount: cfg.response_count || 0,
    lastResponseAt: cfg.last_response_at || null,
    viewCount: cfg.view_count || 0,
    startCount: cfg.start_count || 0,
  };
}

export function surveyToInstrumentPayload(survey) {
  const primaryLanguage = normalizeAppLanguage(
    survey.primaryLanguage || survey.editingLanguage || "en"
  );
  const translations = { ...(survey.translations || {}) };
  const primaryLocale =
    translations[primaryLanguage] || snapshotFormToLocale(survey);
  const questions = normalizeQuestions(survey.questions);

  const config = {
    widget_header_title: (primaryLocale.widgetHeaderTitle || "").trim(),
    show_branding: survey.showBranding !== false,
    request_contact_details: !!survey.requestContactDetails,
    contact_details_required: survey.requestContactDetails
      ? survey.contactDetailsRequired === true
      : false,
    request_consent: !!survey.requestConsent && !survey.startConsent,
    start_consent: {
      enabled: !!survey.startConsent,
      text: (survey.startConsentText || "").trim(),
      privacy_url: (survey.startConsentPrivacyUrl || "").trim(),
    },
    one_response_per_email: !!survey.oneResponsePerEmail,
    ...closeSettingsToConfig(survey),
    response_mode:
      survey.responseMode === SURVEY_RESPONSE_MODE.VOICE ? SURVEY_RESPONSE_MODE.VOICE : SURVEY_RESPONSE_MODE.TEXT,
    // Language of the questions and of the public page's buttons and messages.
    primary_language: primaryLanguage,
    questions,
  };

  if (questions.some((q) => q.type === QUESTION_TYPE.VIDEO)) {
    config.video_retention_days = Math.max(1, Math.min(Number(survey.videoRetentionDays) || 90, 365));
    config.video_transcript = survey.videoTranscript === true;
    const contactEmail = (survey.videoContactEmail || "").trim();
    if (contactEmail) config.video_contact_email = contactEmail;
  }
  if (survey.primaryColor) {
    config.primary_color = survey.primaryColor;
  }
  if (survey.logoUrl) {
    config.logo_url = (survey.logoUrl || "").trim();
  }
  if (survey.popupPosition) {
    config.popup_position = survey.popupPosition;
  }
  config.flow_mode =
    survey.flowMode === SURVEY_FLOW_MODE.SINGLE_PAGE
      ? SURVEY_FLOW_MODE.SINGLE_PAGE
      : SURVEY_FLOW_MODE.STEPPED;
  const thankYou = (survey.thankYouMessage || "").trim();
  if (thankYou) {
    config.thank_you_message = thankYou;
  }

  if (survey.multilingualEnabled) {
    config.multilingual_enabled = true;
    Object.assign(
      config,
      translationsToConfigPayload(primaryLanguage, translations)
    );
  }

  return {
    type: "survey",
    title: (primaryLocale.name || survey.name || "").trim(),
    description: (primaryLocale.description || survey.description || "").trim() || null,
    status: survey.status,
    config,
  };
}

/** The kiosk link of a survey link: the same address with its kiosk key (pivony-api survey_kiosk). */
export const kioskSurveyUrl = (fullUrl, kioskKey) => {
  if (!fullUrl || !kioskKey) return "";
  return `${fullUrl}${fullUrl.includes("?") ? "&" : "?"}kiosk=${encodeURIComponent(kioskKey)}`;
};
